import React from "react";
import BigNumber from "bignumber.js";
import { Col, Row } from "react-bootstrap";

import { useBackendPlatform } from "hooks";
import { backendPlatformsWithFrontendPlatforms } from "mappers";
import { ProgramDataType } from "types";
import { TerminalProductNameTypes } from "types/enums";
import { floatWithComma2Normalizer } from "validators";

import SubscriptionOptionView from "./subscriptionOptionView";
import TokenizationView from "./tokenizationView";
import { TransarmorView, ProgramView } from "..";
import TextBlock from "../components/textBlock";

import styles from "../views.module.scss";

type PointOfSaleSoftwareData = {
	product: { [key: string]: any };
	authorizationVersion: string;
	package: { [key: string]: any };
	setUpFee: string;
	packagePrice: string;
	pos_billed: string;
	nameIfOther: string;
	isTransarmorEnabled: string;
	transarmorMonthlyFee: string;
	transarmorPerTransactionFee: string;
	fileBuild: any;
	isTsysTokenizationEnabled: string;
	merchantType: { [key: string]: any };
	subscriptions?: { totalPrice?: number; discount?: number };
};

type PointOfSaleSoftwareViewProps = {
	data: { pointOfSale: PointOfSaleSoftwareData } & ProgramDataType;
};

const PointOfSaleSoftwareView = ({ data }: PointOfSaleSoftwareViewProps) => {
	const {
		product,
		authorizationVersion,
		nameIfOther,
		merchantType,
		subscriptions,
	} = data?.pointOfSale || {};

	const isProductNailSoft =
		TerminalProductNameTypes.NailSoft.name === product?.name;
	const isProductCalendarise =
		TerminalProductNameTypes.Calendarise.name === product?.name;

	const {
		isBackEndPlatformOmaha,
		isBackEndPlatformNorth,
		isBackEndPlatformTsys,
	} = useBackendPlatform(data?.backEndPlatform);

	const displayFrontEndPlatform =
		backendPlatformsWithFrontendPlatforms.includes(data?.backEndPlatform);

	const discount = new BigNumber(subscriptions?.discount || 0);
	const isDiscountZero = discount.isZero();

	return (
		<>
			<ProgramView
				data={data}
				displayFrontEndPlatform={displayFrontEndPlatform}
			/>
			<Row>
				<Col xl={3}>
					<TextBlock
						label="Product"
						value={product?.displayName}
						dataAutotest="product"
					/>
				</Col>
				{(isBackEndPlatformOmaha || isBackEndPlatformNorth) && (
					<Col xl={3}>
						<TextBlock
							label="Authorization Version"
							value={authorizationVersion}
							dataAutotest="authorizationVersion"
						/>
					</Col>
				)}
			</Row>
			{isProductNailSoft || isProductCalendarise ? (
				<>
					<TextBlock
						label="Merchant Type"
						value={merchantType?.displayName}
						dataAutotest="merchantType"
					/>
					<div className="mt-3">
						<TextBlock label="Subscription Options" isNoColon />
					</div>
					<div className={styles.subscriptionOptionWrap}>
						<SubscriptionOptionView subscriptions={subscriptions} />
					</div>
					{!isDiscountZero && (
						<TextBlock
							label="Discount"
							value={floatWithComma2Normalizer(
								subscriptions?.discount || 0,
								"$",
							)}
							dataAutotest="totalPrice"
						/>
					)}
					<TextBlock
						label="Total Price"
						value={floatWithComma2Normalizer(
							subscriptions?.totalPrice || 0,
							"$",
						)}
						dataAutotest="totalPrice"
					/>
				</>
			) : (
				<TextBlock
					label="Name"
					value={nameIfOther}
					dataAutotest="nameIfOther"
				/>
			)}

			{(isBackEndPlatformOmaha || isBackEndPlatformNorth) && (
				<TransarmorView data={data?.pointOfSale} />
			)}
			{isBackEndPlatformTsys && (
				<TokenizationView data={data?.pointOfSale} />
			)}
		</>
	);
};

export default PointOfSaleSoftwareView;
